/**
 * Compact readiness score history chart — one line per kind
 * (checklist normalised to %; workbook normalised to %) so both can share
 * a Y axis and users can see their curve climbing.
 */
import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { TrendingUp } from "lucide-react";

export default function ScoreHistoryChart({ history, activeKind }) {
  const data = useMemo(() => {
    // Group by day for a clean X axis; each row can carry either kind's %.
    const byDay = new Map();
    for (const h of history || []) {
      const pct = Math.round((h.score / (h.total || 1)) * 100);
      const row = byDay.get(h.day) || { day: h.day };
      row[h.kind] = pct;
      byDay.set(h.day, row);
    }
    return Array.from(byDay.values()).sort((a, b) => (a.day < b.day ? -1 : 1));
  }, [history]);

  if (data.length < 2) return null; // need at least two points to draw a trend

  const first = data[0];
  const last = data[data.length - 1];
  const change =
    (last[activeKind] || 0) - (first[activeKind] || first.checklist || first.workbook || 0);

  return (
    <section
      data-testid="score-history"
      className="mt-6 bg-white border border-[#E5E2DC] rounded-2xl p-5 sm:p-6 print:break-inside-avoid"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] inline-flex items-center gap-1.5">
          <TrendingUp size={11} /> Your score over time
        </div>
        {change !== 0 && (
          <span
            data-testid="score-history-delta"
            className={`text-xs font-mono-num ${change > 0 ? "text-[#2B5339]" : "text-[#C26747]"}`}
          >
            {change > 0 ? "+" : ""}
            {change} points since first check-in
          </span>
        )}
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#F0EFEA" strokeDasharray="2 4" />
            <XAxis
              dataKey="day"
              tickFormatter={(d) => d.slice(5)}
              tick={{ fontSize: 10, fill: "#5C6B62" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: "#5C6B62" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E2DC" }}
              formatter={(v, name) => [`${v}%`, name === "checklist" ? "Checklist" : "Workbook"]}
              labelFormatter={(d) => d}
            />
            <Line
              type="monotone"
              dataKey="checklist"
              stroke="#2B5339"
              strokeWidth={2}
              dot={{ r: 3, fill: "#2B5339" }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="workbook"
              stroke="#C26747"
              strokeWidth={2}
              dot={{ r: 3, fill: "#C26747" }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-2 text-[11px] text-[#5C6B62]">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[#2B5339]" /> Checklist (%)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[#C26747]" /> Workbook (%)
        </span>
      </div>
    </section>
  );
}
